import { faArrowLeft, faArrowRight, faChartLine, faDatabase, faMagnifyingGlass, faMessage, faUser, faToolbox } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from 'react';

interface NavMenuProps {
    isSubscribed: boolean;
    isAdmin: boolean;
    isLocalDev?: boolean;
}

const NavMenu = ({ isAdmin, isSubscribed, isLocalDev = false }: NavMenuProps) => {
    const showAnalytics = isSubscribed || isAdmin;
    const [menuOpen, setMenuOpen] = useState(false);
    const [addClass, setAddClass] = useState("menu__link-text-hidden");
    const location = useLocation();

    const handleNavMenu = () => {
        setMenuOpen(!menuOpen);
        setAddClass(menuOpen ? "menu__link-text-hidden" : "");
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const navMenu = document.querySelector('.nav-menu');
            if (navMenu && !navMenu.contains(event.target as Node)) {
                setMenuOpen(false);
                setAddClass("menu__link-text-hidden");
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
        setAddClass("menu__link-text-hidden");
    }, [location]);

    const linkTextClass = `nav-menu__link-text ${addClass}`;

    return (
        <div className={`nav-menu ${menuOpen ? 'expanded' : ''}`}>
            <div className="nav-menu__item nav-menu__item--toggle">
                <button
                    type="button"
                    onClick={handleNavMenu}
                    className="nav-menu__link nav-menu__toggle"
                    aria-label={menuOpen ? "Collapse menu" : "Expand menu"}
                    data-tooltip={menuOpen ? "Collapse" : "Expand"}
                >
                    <FontAwesomeIcon
                        className="nav-menu__link-icon"
                        icon={menuOpen ? faArrowLeft : faArrowRight}
                    />
                    <span className={linkTextClass}>
                        {menuOpen ? "Collapse" : "Expand"}
                    </span>
                    {isLocalDev && <span className="dev-badge-nav">DEV</span>}
                </button>
            </div>

            <nav className="nav-menu__main" aria-label="Main">
                <div className={`nav-menu__item ${location.pathname === '/sponsors' ? 'active' : ''}`}>
                    <Link
                        to="/sponsors"
                        className="nav-menu__link"
                        data-tooltip="Sponsors"
                    >
                        <FontAwesomeIcon className="nav-menu__link-icon" icon={faDatabase} />
                        <span className={linkTextClass}>Sponsors</span>
                    </Link>
                </div>
                {showAnalytics && (
                    <div className={`nav-menu__item ${location.pathname === '/analytics' ? 'active' : ''}`}>
                        <Link
                            to="/analytics"
                            className="nav-menu__link"
                            data-tooltip="Analytics"
                        >
                            <FontAwesomeIcon className="nav-menu__link-icon" icon={faChartLine} />
                            <span className={linkTextClass}>Analytics</span>
                        </Link>
                    </div>
                )}
                <div className={`nav-menu__item ${location.pathname === '/tools' ? 'active' : ''}`}>
                    <Link
                        to="/tools"
                        className="nav-menu__link"
                        data-tooltip={(isSubscribed || isAdmin) ? 'Tools' : 'Free Tools'}
                    >
                        <FontAwesomeIcon className="nav-menu__link-icon" icon={faToolbox} />
                        <span className={linkTextClass}>{(isSubscribed || isAdmin) ? 'Tools' : 'Free Tools'}</span>
                    </Link>
                </div>
                <div className={`nav-menu__item ${location.pathname === '/profile' ? 'active' : ''}`}>
                    <Link
                        to="/profile"
                        className="nav-menu__link"
                        data-tooltip="Account & Billing"
                    >
                        <FontAwesomeIcon className="nav-menu__link-icon" icon={faUser} />
                        <span className={linkTextClass}>Account & Billing</span>
                    </Link>
                </div>
            </nav>

            <div className="nav-menu__footer">
                <Link
                    to="/feedback"
                    className={`nav-menu__feedback-link${location.pathname === '/feedback' ? ' nav-menu__feedback-link--active' : ''}`}
                    data-tooltip="Send Feedback"
                >
                    <FontAwesomeIcon className="nav-menu__feedback-icon" icon={faMessage} />
                    <span className={linkTextClass}>Send Feedback</span>
                </Link>
                {isAdmin && (
                    <div className={`nav-menu__item nav-menu__item--admin ${location.pathname === '/admin' ? 'active' : ''}`}>
                        <Link
                            to="/admin"
                            className="nav-menu__link"
                            data-tooltip="Admin"
                        >
                            <FontAwesomeIcon className="nav-menu__link-icon" icon={faMagnifyingGlass} />
                            <span className={linkTextClass}>Admin</span>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NavMenu;
